'use client';

import classnames from 'classnames';
import dayjs from '@lib/dayjs';
import { useEffect, useState, useMemo } from 'react';
import { FixtureCardSlotted as V3FixtureCardSlotted } from '@stadionhq-v3/football-ui';
import FixtureCardSecondSlot from '@components/molecules/fixture-card-second-slot';
import FixtureCardFourthSlot from '@components/molecules/fixture-card-fourth-slot';
import FixtureCardFirstSlot from '@components/molecules/fixture-card-first-slot';
import { Link } from '@stadionhq-v3/base-ui';
import styles from './fixtureCardSlotted.module.scss';
import { FixtureCardSlottedProps } from './fixtureCardSlotted.types';

const FixtureCardSlotted = ({
  nativeProps,
  classNames,
  fixtureProps,
  locale,
  kickOffUtc,
  ...props
}: FixtureCardSlottedProps) => {
  const classes = classnames(
    [styles.tokens, styles.fixtureCardSlottedContainer],
    classNames
  );

  const [isClient, setIsClient] = useState(false);

  const djs = dayjs(locale);
  const date = djs(kickOffUtc).tz(djs.tz.guess());

  const fixturePropsWithTimezones = useMemo(
    () => ({
      ...fixtureProps,
      date: date.format('Do MMM YYYY'),
      kickoffTime: date.format('HH:mm'),
    }),
    [date, fixtureProps]
  );

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Show simple skeleton while waiting for client-side hydration
  if (!isClient) {
    return (
      <div className={classes} {...nativeProps}>
        <div className={styles.skeleton} />
      </div>
    );
  }

  const flexConfig = {
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
  } as const;

  const slottedFixtureCard = (
    <V3FixtureCardSlotted
      flex={flexConfig}
      classNames={[classnames([styles.fixtureCardSlotted])]}
    >
      <V3FixtureCardSlotted.FirstSlot>
        <FixtureCardFirstSlot
          matchStatus={fixturePropsWithTimezones.matchStatus}
          labels={fixturePropsWithTimezones.labels}
        />
      </V3FixtureCardSlotted.FirstSlot>
      <V3FixtureCardSlotted.SecondSlot>
        <FixtureCardSecondSlot
          fixtureProps={fixturePropsWithTimezones}
          {...props}
        />
      </V3FixtureCardSlotted.SecondSlot>
      <V3FixtureCardSlotted.FourthSlot>
        <FixtureCardFourthSlot
          ctaLinks={fixturePropsWithTimezones.ctaLinks}
          addToCalendarData={fixturePropsWithTimezones.addToCalendarData}
          matchCentreCta={fixturePropsWithTimezones.matchCentreCta}
        />
      </V3FixtureCardSlotted.FourthSlot>
    </V3FixtureCardSlotted>
  );

  const matchCentreCtaUrl = fixturePropsWithTimezones.matchCentreCta?.url;

  if (matchCentreCtaUrl) {
    return (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <Link
        url={matchCentreCtaUrl}
        classNames={[classnames([classes, styles.cardLink])]}
        useNext
        title={fixturePropsWithTimezones.matchCentreCta?.title || ''}
      >
        {slottedFixtureCard}
      </Link>
    );
  }

  return (
    <div className={classes} {...nativeProps}>
      {slottedFixtureCard}
    </div>
  );
};

export default FixtureCardSlotted;
